import * as React from 'react';
import { Box, Flex } from 'rebass';
import * as Yup from 'yup';
import { Formik, Field, FormikConfig } from 'formik';
import { useLocation } from 'react-router-dom';

import {
  FormHeader,
  FormSubHeader,
  ErrorNotification,
  EmailField,
  ButtonLarge,
  BackToLogin,
} from '@mdlinx/component-library';
import { Result, failed, Auth } from '@mdlinx/shared';

import { useSendLoginLinkEmail } from 'lib/effect-hooks';
import { FormContainer } from 'app/components/layout/form-container';
import { trackEvent, getLocationDetails } from 'lib/analytics';

function sendMessage(message: Auth.EmailEvent) {
  if (window.parent) {
    window.parent.postMessage(message, '*');
  }
}

export const AutoLoginLinkForm: React.FC<{
  onClickBack?: () => void;
  onSuccess: (actionType: string) => void;
}> = ({ onClickBack, onSuccess }) => {
  const sendLoginLinkEmail = useSendLoginLinkEmail();
  const [result, setResult] = React.useState<Result<string, string> | undefined>();
  const location = useLocation();

  const onSubmit: FormikConfig<{ email: string; actionType: string }>['onSubmit'] = ({ email, actionType }) => {
    setResult(undefined);
    trackEvent('auth_click', { action: 'onSubmit', ...getLocationDetails(location.pathname, location.search) });
    switch (actionType) {
      case 'login-link': {
        return sendLoginLinkEmail(email)
          .then(() => {
            sendMessage(Auth.loginLinkSent);
            onSuccess(actionType);
          })
          .catch((error: any) => {
            setResult(failed('Failed to send email.'));
            console.error(error);
          });
      }
      default: {
        console.log('BAD ACTION TYPE');
        return;
      }
    }
  };

  const errorMessage =
    result &&
    result.fold(
      message => message,
      message => message,
    );

  return (
    <FormContainer alternativeScene="sign-up">
      <Flex flexDirection="column">
        <FormHeader>Auto-login link</FormHeader>
        <Box mt="12px" />
        <FormSubHeader>
          Enter the email address associated with your account, and we’ll send an auto-login link to your inbox.
        </FormSubHeader>
        <Box mt="20px" />
        {errorMessage && (
          <Box my="10px">
            <ErrorNotification errorMessage={errorMessage} />
          </Box>
        )}
        <Box width="100%" mb={['50px']}>
          <Formik
            initialValues={{ email: '', actionType: '' }}
            validationSchema={Yup.object({
              email: Yup.string()
                .email('Invalid email addresss')
                .required('Required'),
            })}
            onSubmit={onSubmit}
          >
            {formik => (
              <>
                <EmailField
                  onSubmitClicked={() => formik.handleSubmit()}
                  isSubmitButtonDisabled={!(formik.dirty && formik.isValid)}
                />
                <Field type="hidden" name="actionType" />
                <Box mt="10px" />
                <ButtonLarge
                  type="submit"
                  disabled={!(formik.dirty && formik.isValid)}
                  onClick={() => {
                    formik.setFieldValue('actionType', 'login-link');
                    formik.handleSubmit();
                  }}
                >
                  Continue
                </ButtonLarge>
                <Box mt="10px" />
              </>
            )}
          </Formik>
        </Box>
        <BackToLogin onClick={onClickBack} />
      </Flex>
    </FormContainer>
  );
};
